import React, { useContext, useEffect, useState } from 'react'
import { useFormContext, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Dimmer, Grid, Label, LabelDetail, Popup, SemanticWIDTHS, Table } from 'semantic-ui-react'
import { FormFields } from '~/components/FormFields'
import cloneDeep from 'lodash.clonedeep'
import { StyledFirstColumn } from '../style'
import { CO2OptionsType } from '~/api/services/co2'
import CustomIcon from '~/components/CustomIcon'
import BarleyModal from './BarleyModal'
import { formatNumber } from '~/utils/helpers'
import { getSumOfFilledFeeds, getTransformFeedGroups, prepareDataForPayload, redistributeDays } from '../../utils'
import CO2ClimateQuery from '~/QueryHooks/CO2ClimateQuery'
import { useParams } from 'react-router-dom'
import { StyledEnergyWrapper, StyledGridColumn, StyledLoader, StyledTable } from './style'
import InfoPopup from '~/components/InfoPopup'
import { FeedPageContext } from '../..'
import { monthsArray } from '../VariableCosts/FeedGroups/Phases'
interface Props {
  milkOptions: CO2OptionsType
}

const FeedRations: React.FC<Props> = (props) => {
  const { milkOptions } = props
  const { control, getValues } = useFormContext()
  const { t } = useTranslation()
  const { id, farmerId } = useParams()

  const { energyCalcData, setEnergyCalcData, loadingEnergyData } = useContext(FeedPageContext)

  const energyLabels = [
    {
      name: t('Conservation needs'),
      key: 'maintenanceNeedsEnergy'
    },
    {
      name: t('Reproductive needs'),
      key: 'reproductionNeedsEnergy'
    },
    {
      name: t('Power requirement'),
      key: 'powerRequirementEnergy'
    }
  ]

  const panesOptions = [
    {
      title: t('Coarse Feeds'),
      key: 'coarseFeeds'
    },
    {
      title: t('Power and mineral feed'),
      key: 'concentratedFeeds'
    },
    {
      title: t('Juicy Feeds'),
      key: 'juicyFeeds'
    }
  ]
  const payload = dataParse()

  const {
    data: energyData,
    isFetching: isFetchingData
  } = CO2ClimateQuery.getEnergyData(
    id,
    { ...payload, farmerId }
  )

  const watchPhaseEnabled = useWatch({ control, name: 'phaseFeeding' })
  const feedGroups = useWatch({ control, name: 'feedGroups' })

  const [feedGroupsData, setFeedGroupsData] = useState([])

  useEffect(() => {
    if (energyCalcData) {
      setEnergyCalcData(null)
    }
  }, [feedGroups, watchPhaseEnabled])

  useEffect(() => {
    if (watchPhaseEnabled) {
      setFeedGroupsData(getTransformFeedGroups(feedGroups))
    } else {
      setFeedGroupsData(feedGroups)
    }
  }, [watchPhaseEnabled])

  const [parsedData, setParsedData] = useState(null)
  const [barleyData, setBarleyData] = useState(null)

  function dataParse() {
    const data = cloneDeep(getValues())
    const calvingPeriodDays = getValues('calvingPeriod')
    // const feedGroupsData = data.phaseFeeding ? getTransformFeedGroups(data.feedGroups) : data.feedGroups
    data.feedGroups = data.feedGroups?.length > 1 ? redistributeDays(data.feedGroups, calvingPeriodDays) : data.feedGroups
    return prepareDataForPayload(data)
  }

  const getParsedData = (data) => {
    const result = {
      coarseFeeds: [],
      concentratedFeeds: [],
      juicyFeeds: []
    };

    if (watchPhaseEnabled) {
      data = getTransformFeedGroups(data)
    }

    data.forEach(item => {
      item.concentratedFeeds.forEach(feed => {
        if (!result.concentratedFeeds.find(item => item.id === feed.id)) {
          const concentratedFeeds = getValues('concentratedFeeds')
          const index = concentratedFeeds?.findIndex(concentratedFeed => concentratedFeed.id === feed.id)
          result.concentratedFeeds.push({ ...feed, dryMatter: concentratedFeeds[index]?.dryMatter, energy: concentratedFeeds[index]?.energy, index });
        }
      });
      item.juicyFeeds.forEach(feed => {
        if (!result.juicyFeeds.find(item => item.id === feed.id)) {
          const juicyFeeds = getValues('juicyFeeds')
          const index = juicyFeeds?.findIndex(juicyFeed => juicyFeed.id === feed.id)
          result.juicyFeeds.push({ ...feed, dryMatter: juicyFeeds[index]?.dryMatter, energy: juicyFeeds[index]?.energy, index });
        }
      });
      item.coarseFeeds.forEach(feed => {
        if (!result.coarseFeeds.find(item => item.id === feed.id)) {
          const coarseFeeds = getValues('coarseFeeds')
          const index = coarseFeeds?.findIndex(coarseFeed => coarseFeed.id === feed.id)
          result.coarseFeeds.push({ ...feed, dryMatter: coarseFeeds[index]?.dryMatter, energy: coarseFeeds[index]?.energy, index });
        }
      });
    });

    return result
  };

  useEffect(() => {
    setParsedData(getParsedData(cloneDeep(feedGroups)))
  }, [])

  function renderTableHeaders(firstColumnTitle) {
    return <Table.Row>
      <Table.HeaderCell width={'4'}>{firstColumnTitle}</Table.HeaderCell>
      {feedGroupsData.map(feedGroup => {
        const month = feedGroup?.months ? monthsArray.find(month => month.key === feedGroup?.months) : ''
        return <Table.HeaderCell key={feedGroup.groupKey}>
          {`${feedGroup?.name || ''} ${month ? `(${month?.label})` : ''}`}
        </Table.HeaderCell>
      })}
    </Table.Row>
  }

  function renderTableBody(rows, key) {
    return <Table.Body>
      {rows.map((feed) => {
        return <Table.Row key={feed.id}>
          <Table.Cell>
            <div>{feed.label}</div>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 5 }}>
              <small style={{ fontSize: '85%' }}>
                {t('[TM: {{dryMatter}} % Energy: {{energy}} MJ NEL/kg TM]',
                  {
                    dryMatter: formatNumber(getValues(key)[feed.index]?.dryMatter?.replace(',', '.'), 2),
                    energy: formatNumber(getValues(key)[feed.index]?.energy?.replace(',', '.'), 2)
                  }
                )}
              </small>
              {feed?.dryMatter && feed?.energy && <Popup
                content={<div>
                  <div>{t('Change')}</div>
                </div>}
                trigger={<CustomIcon
                  name={'pencil'}
                  onClick={() => setBarleyData({
                    index: feed.index,
                    key,
                    feedName: feed.label
                  })}
                  style={{ fontSize: '1.2em' }}
                />}
              />}
            </div>
          </Table.Cell>
          {feedGroupsData.map((feedGroup, indexFeedGrup) => {
            const indexFeed = feedGroup[key].findIndex(value => value.id === feed.id)
            const disabled = indexFeed < 0
            const feedingPhase = getValues(`feedGroups[${feedGroup.feedGroupIndex}].feedingPhases`)
            const phaseFeedingName = watchPhaseEnabled && feedingPhase?.length > 0 ? `feedGroups[${feedGroup.feedGroupIndex}].feedingPhases[${feedGroup.order - 1}].${key}[${indexFeed}].value` : ''
            const indexFeedWithPhase = watchPhaseEnabled ? feedGroup.feedGroupIndex : indexFeedGrup
            const name = watchPhaseEnabled && feedingPhase?.length > 0 ? phaseFeedingName : `feedGroups[${indexFeedWithPhase}].${key}[${indexFeed}].value`
            return <Table.Cell width={Math.floor(12 / feedGroupsData.length) as SemanticWIDTHS} key={name}>
              {!disabled && <FormFields.Input
                disabled={indexFeed < 0}
                placeholder={t('kg FM/animal and day')}
                name={name}
                control={control}
                typeInput={'controled'}
                withoutAsterisk
                rules={{
                  required: true
                }}
                suffix={' kg FM/Tier u. Tag'}
                noErrorMessage
                allowDecimals
                decimalScale={2}
              />
              }
            </Table.Cell>
          })}
        </Table.Row>
      })}
    </Table.Body>
  }

  function renderTable(rows, key, firstColumnTitle) {
    return rows?.length > 0 && <StyledTable>
      <Table.Header>
        {renderTableHeaders(firstColumnTitle)}
      </Table.Header>
      {renderTableBody(rows, key)}
    </StyledTable>
  }

  function parseConcentratedFeeds(concentratedFeeds) {
    return concentratedFeeds.reduce((acc, item) => {
      const { group } = item;
      if (!acc[group]) {
        acc[group] = [];
      }
      acc[group].push(item);
      return acc;
    }, {});
  }

  function getConcentratedFeedTitle(groupIndex) {
    return Object.keys(milkOptions.concentratedFeeds)[groupIndex - 1]
  }

  function renderFeedRations() {
    return Object.keys(parsedData).map(key => {
      const title = panesOptions.find(pane => pane.key === key)?.title
      if (key === 'concentratedFeeds') {
        const groupConcentratedFeeds = parseConcentratedFeeds(parsedData?.concentratedFeeds)
        return <div style={{ margin: '1em 0em' }} key={key}>
          <div>
            {Object.keys(groupConcentratedFeeds).map(key => {
              const firstColumnTitle = <StyledFirstColumn>
                <div>{title}</div>
                <div className='subHeader'>{getConcentratedFeedTitle(key)}</div>
              </StyledFirstColumn>
              return <div style={{ margin: '1em 0em' }} key={groupConcentratedFeeds[key]}>
                {renderTable(Object.values(groupConcentratedFeeds[key]), 'concentratedFeeds', firstColumnTitle)}
              </div>
            })}
          </div>
        </div>
      } else {
        return <div style={{ margin: '1em 0em' }} key={key}>
          {renderTable(Object.values(parsedData[key]), key, title)}
        </div>
      }
    })
  }

  function renderBarleyModal() {
    return <BarleyModal
      barleyData={barleyData}
      handleClose={() => setBarleyData(null)}
    />
  }

  function renderTotal() {
    const sums = feedGroupsData.map(feedGroup => getSumOfFilledFeeds(feedGroup))
    return <StyledTable>
      <Table.Header>
        <Table.HeaderCell width={'4'}>{t('Total')}</Table.HeaderCell>
        {sums.map((sum) => {
          return <Table.Cell width={Math.floor(12 / feedGroupsData.length) as SemanticWIDTHS} key={`sum${Math.random()}`}>
            <div style={{ marginLeft: 14 }}>
              {`${formatNumber(sum, 2)} kg FM/Tier u. Tag`}
            </div>
          </Table.Cell>
        })}
      </Table.Header>
    </StyledTable>
  }

  function renderSpinner() {
    return <Dimmer inverted active={true} style={{ background: 'transparent' }}>
      <StyledLoader active={true} />
    </Dimmer>
  }

  function renderEnergyNeeds() {
    return <Grid columns={2}>
      {energyLabels.map(energyLabel => {
        const value = energyData ? energyData[energyLabel.key] : ''
        return <Grid.Row style={{ fontWeight: 'bold', paddingTop: '0.3rem', paddingBottom: '0.3rem' }} key={energyLabel.name} >
          <Grid.Column>{energyLabel.name}:</Grid.Column>
          <StyledGridColumn>{isFetchingData ? renderSpinner() : `${formatNumber(value, 0)} MJ NEL/Jahr`}</StyledGridColumn>
        </Grid.Row>
      })}
    </Grid>
  }

  function renderTotalEnergy() {
    return <StyledEnergyWrapper>
      {energyCalcData?.totalEnergyWithAddition && <Label as='a'>
        {t('Calculated feed energy')}:
        <LabelDetail style={{ position: 'relative', minWidth: '1.3rem' }}>
          {loadingEnergyData ? renderSpinner() : `${formatNumber(energyCalcData?.totalEnergyWithAddition, 0)} MJ NEL/Jahr`}
        </LabelDetail>
      </Label>}
      <div style={{ display: 'flex', alignItems: 'center' }}>
        <Label as='a'>
          {t('Total energy requirement')}:
          <LabelDetail style={{ position: 'relative', minWidth: '1.3rem' }}>
            {isFetchingData ? renderSpinner() : `${formatNumber(energyData?.totalEnergy, 0)} MJ NEL/Jahr`}
          </LabelDetail>
        </Label>
        <InfoPopup
          content={renderEnergyNeeds()}
          styleIcon={{ fontSize: '1.5em', marginLeft: 5 }}
          position={'top left'}
          minWidth={350}
        /></div>
    </StyledEnergyWrapper>
  }

  return <div>
    {renderTotalEnergy()}
    <div>
      {parsedData && renderFeedRations()}
      {renderTotal()}
      {renderBarleyModal()}
    </div>
  </div>
}
export default FeedRations